import {
  AlertCircleIcon,
  CheckmarkCircle02Icon
} from "@hugeicons-pro/core-solid-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { cx } from "shared/utils";
import { AssetItem } from "views/applications-id-report/components/SmartRender/AssetItem";
import { parseDocStatus } from "views/applications-id-report/utils/parseDocStatus";
import { Asset } from "shared/app/IdentityVerificationReport/types";
import { ReasonCode } from "types/landlord/reports";

interface DocumentCardProps {
  score: number;
  asset?: Asset["asset"];
  reasonCodes?: ReasonCode[];
  ignorePrint?: boolean;
}

export const DocumentCard = ({
  score,
  asset,
  reasonCodes,
  ignorePrint
}: DocumentCardProps) => {
  const status = parseDocStatus(score);

  return (
    <div className="flex gap-4">
      {asset ? (
        <AssetItem
          ignorePrint={ignorePrint}
          url={asset.url || ""}
          content_type={asset.content_type || ""}
          filename={asset.filename || ""}
          assetName={asset.filename || ""}
          // Heights sum of: reason codes header + item height * quantity
          thumbnailHeight={75 + 53 * (reasonCodes?.length || 1)}
        />
      ) : null}
      <div
        className={cx(
          "flex flex-col w-full rounded-md p-6 gap-2 border border-alpha-100",
          status === "Accepted" ? "bg-success-50" : "",
          status === "Warning" ? "bg-warning-50" : "",
          status === "Rejected" ? "bg-danger-50" : ""
        )}
      >
        {/* Icon */}
        {status === "Accepted" ? (
          <HugeiconsIcon
            icon={CheckmarkCircle02Icon}
            size={20}
            className="shrink-0 text-success-500"
          />
        ) : (
          <HugeiconsIcon
            icon={AlertCircleIcon}
            size={20}
            className={cx(
              "shrink-0",
              status === "Warning" ? "text-warning-500" : "",
              status === "Rejected" ? "text-danger-500" : ""
            )}
          />
        )}

        <p className="text-content-400 text-sm font-medium">
          <span className="text-content-1000">
            Authenticity score {score || "-"}
            /100:
          </span>{" "}
          {status === "Accepted"
            ? "We did not find evidence of tampering / fraud this document"
            : ""}
          {status === "Warning"
            ? "We recommend manually reviewing this document as suspicious signals were found"
            : ""}
          {status === "Rejected"
            ? "Based on the below reason codes, we do not believe this document to be authentic"
            : ""}
        </p>

        {reasonCodes?.map((item, idx) => {
          return (
            <p
              key={
                item.code || `${asset?.filename}-${score}-reason-code-${idx}`
              }
              className={cx(
                "font-normal text-content-1000 text-sm first-letter:capitalize",
                idx === 0 && "border-t border-alpha-40 pt-4"
              )}
            >
              {item.description}: {item.confidence || "-"} confidence
            </p>
          );
        })}
      </div>
    </div>
  );
};
